import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

interface SEOHeadProps {
  title?: string;
  description?: string;
  keywords?: string;
  ogImage?: string;
  canonical?: string;
  noindex?: boolean;
}

const SEOHead = ({ 
  title = 'ASC Fechten Wien - Akademischer Sportclub',
  description = 'Der Akademische Sportclub Fechten Wien - Tradition trifft Moderne. Professionelles Fechttraining für alle Altersgruppen seit 1970.',
  keywords = 'Fechten, Wien, Sportverein, Training, Turniere, ASC, Akademischer Sportclub',
  ogImage = '/lovable-uploads/ea6c2094-bd02-4d67-8d6b-4895033aa4fb.png',
  canonical,
  noindex = false
}: SEOHeadProps) => {
  const location = useLocation();
  const currentUrl = `https://asc-fechten-wien.lovable.app${location.pathname}`;
  const canonicalUrl = canonical || currentUrl;

  useEffect(() => {
    // Update title
    document.title = title;

    // Update meta tags
    const updateMetaTag = (name: string, content: string, property = false) => {
      const selector = property ? `meta[property="${name}"]` : `meta[name="${name}"]`;
      let meta = document.querySelector(selector) as HTMLMetaElement;
      
      if (!meta) {
        meta = document.createElement('meta');
        if (property) {
          meta.setAttribute('property', name);
        } else {
          meta.setAttribute('name', name);
        }
        document.head.appendChild(meta);
      }
      meta.content = content;
    };

    // Basic meta tags
    updateMetaTag('description', description);
    updateMetaTag('keywords', keywords);
    updateMetaTag('author', 'ASC Fechten Wien');

    // Open Graph tags
    updateMetaTag('og:title', title, true);
    updateMetaTag('og:description', description, true);
    updateMetaTag('og:image', ogImage, true);
    updateMetaTag('og:url', currentUrl, true);
    updateMetaTag('og:type', 'website', true);
    updateMetaTag('og:site_name', 'ASC Fechten Wien', true);

    // Twitter tags
    updateMetaTag('twitter:card', 'summary_large_image');
    updateMetaTag('twitter:title', title);
    updateMetaTag('twitter:description', description);
    updateMetaTag('twitter:image', ogImage);

    // Robots
    if (noindex) {
      updateMetaTag('robots', 'noindex, nofollow');
    } else {
      updateMetaTag('robots', 'index, follow');
    }

    // Canonical URL
    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      document.head.appendChild(canonical);
    }
    canonical.href = canonicalUrl;

    // JSON-LD Structured Data
    const jsonLd = {
      "@context": "https://schema.org",
      "@type": "SportsClub",
      "name": "ASC Fechten Wien",
      "description": description,
      "url": currentUrl,
      "logo": ogImage,
      "sameAs": [],
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Wien",
        "addressCountry": "AT"
      },
      "sport": "Fechten",
      "foundingDate": "1970"
    };

    let structuredData = document.querySelector('script[type="application/ld+json"]') as HTMLScriptElement;
    if (!structuredData) {
      structuredData = document.createElement('script');
      structuredData.type = 'application/ld+json';
      document.head.appendChild(structuredData);
    }
    structuredData.textContent = JSON.stringify(jsonLd);

  }, [title, description, keywords, ogImage, currentUrl, canonicalUrl, noindex]);

  return null;
};

export default SEOHead;